import React, { ReactElement, useMemo } from 'react';
import { StatusBlock } from '../../../common/status-block';
import moment from 'moment';
import {
  CleanCustomsDeliveryOrder,
  CustomsDeliveryOrderStatus,
} from '../../../../export-types/cleaned-types';
import { FormattedMessage } from 'react-intl';
import { ChildStatusBlock } from '../../../common/child-status-block';

type Props = {
  order: CleanCustomsDeliveryOrder;
};

export const AdminCustomsDeliveryOrderStatusTable: React.FC<Props> = ({
  order,
}) => {
  const actualStatuses = useMemo<{
    current: ReactElement;
    previous: ReactElement | null;
    next: ReactElement | null;
  } | null>(() => {
    const statusOrder: Array<{
      statuses: CustomsDeliveryOrderStatus | CustomsDeliveryOrderStatus[];
      element: ReactElement;
    }> = [
      {
        statuses: 'created',
        element: (
          <StatusBlock
            title={
              <FormattedMessage
                defaultMessage="Черновик"
                id="customsDeliveryOrder.status-label.created"
                description="Статус заказа"
              />
            }
            subtitle={
              order.createdAt &&
              moment(order.createdAt).format('DD.MM.YYYY, HH:mm')
            }
            status={
              order.status === 'created'
                ? 'active'
                : order.createdAt
                  ? 'done'
                  : undefined
            }
            connector={true}
            key={'createdAt'}
          />
        ),
      },
      {
        statuses: 'preparing',
        element: (
          <StatusBlock
            title={
              <FormattedMessage
                defaultMessage="Новый"
                id="customsDeliveryOrder.status-label.preparing"
                description="Статус заказа"
              />
            }
            subtitle={
              order.preparingAt &&
              moment(order.preparingAt).format('DD.MM.YYYY, HH:mm')
            }
            status={
              order.status === 'preparing'
                ? 'active'
                : order.preparingAt
                  ? 'done'
                  : undefined
            }
            connector={true}
            key={'preparingAt'}
          />
        ),
      },
      {
        statuses: ['codeSelected', 'calculationReady', 'protectCalculation'],
        element: (
          <StatusBlock
            title={
              <FormattedMessage
                defaultMessage="В работе"
                id="customsDeliveryOrder.status-label.inWork"
                description="Статус заказа"
              />
            }
            subtitle={
              order.codeSelectedAt &&
              moment(order.codeSelectedAt).format('DD.MM.YYYY, HH:mm')
            }
            status={
              [
                'codeSelected',
                'calculationReady',
                'protectCalculation',
              ].includes(order.status)
                ? 'active'
                : order.protectCalculationAt
                  ? 'done'
                  : undefined
            }
            connector={true}
            subStatuses={
              <div className={'mt-2'}>
                <ChildStatusBlock
                  title={
                    <FormattedMessage
                      defaultMessage="Подобрать код"
                      id="customsDeliveryOrder.status-label.codeSelected"
                      description="Статус заказа"
                    />
                  }
                  subtitle={
                    order.codeSelectedAt &&
                    moment(order.codeSelectedAt).format('DD.MM.YYYY, HH:mm')
                  }
                  status={
                    order.status === 'codeSelected'
                      ? 'active'
                      : order.codeSelectedAt
                        ? 'done'
                        : undefined
                  }
                  connector={true}
                  key={'codeSelectedAt'}
                />
                <ChildStatusBlock
                  title={
                    <FormattedMessage
                      defaultMessage="Подготовлен пред.расчет"
                      id="customsDeliveryOrder.status-label.calculationReady"
                      description="Статус заказа"
                    />
                  }
                  subtitle={
                    order.calculationReadyAt &&
                    moment(order.calculationReadyAt).format('DD.MM.YYYY, HH:mm')
                  }
                  status={
                    order.status === 'calculationReady'
                      ? 'active'
                      : order.calculationReadyAt
                        ? 'done'
                        : undefined
                  }
                  connector={true}
                  key={'calculationReadyAt'}
                />
                <ChildStatusBlock
                  title={
                    <FormattedMessage
                      defaultMessage="Защитить расчет"
                      id="customsDeliveryOrder.status-label.protectCalculation"
                      description="Статус заказа"
                    />
                  }
                  subtitle={
                    order.protectCalculationAt &&
                    moment(order.protectCalculationAt).format(
                      'DD.MM.YYYY, HH:mm',
                    )
                  }
                  status={
                    order.status === 'protectCalculation'
                      ? 'active'
                      : order.protectCalculationAt
                        ? 'done'
                        : undefined
                  }
                  key={'protectCalculationAt'}
                />
              </div>
            }
            key={'inWorkStatuses'}
          />
        ),
      },
      {
        statuses: ['prepareDocs', 'declarant', 'prepareInvoice'],
        element: (
          <StatusBlock
            title={
              <FormattedMessage
                defaultMessage="Документы"
                id="customsDeliveries.tab-label.documents"
                description="Статус заказа/Вкладка"
              />
            }
            subtitle={
              order.prepareDocsAt &&
              moment(order.prepareDocsAt).format('DD.MM.YYYY, HH:mm')
            }
            status={
              ['prepareDocs', 'declarant', 'prepareInvoice'].includes(
                order.status,
              )
                ? 'active'
                : order.prepareInvoiceAt
                  ? 'done'
                  : undefined
            }
            connector={true}
            key={'codeSelectedAt'}
            subStatuses={
              <div className={'mt-2'}>
                <ChildStatusBlock
                  title={
                    <FormattedMessage
                      defaultMessage="Подготовка документов"
                      id="customsDeliveryOrder.status-label.prepareDocs"
                      description="Статус заказа"
                    />
                  }
                  subtitle={
                    order.prepareDocsAt &&
                    moment(order.prepareDocsAt).format('DD.MM.YYYY, HH:mm')
                  }
                  status={
                    order.status === 'prepareDocs'
                      ? 'active'
                      : order.prepareDocsAt
                        ? 'done'
                        : undefined
                  }
                  connector={true}
                  key={'prepareDocsAt'}
                />
                <ChildStatusBlock
                  title={
                    <FormattedMessage
                      defaultMessage="Декларант"
                      id="customsDeliveryOrder.status-label.declarant"
                      description="Статус заказа"
                    />
                  }
                  subtitle={
                    order.declarantAt &&
                    moment(order.declarantAt).format('DD.MM.YYYY, HH:mm')
                  }
                  status={
                    order.status === 'declarant'
                      ? 'active'
                      : order.declarantAt
                        ? 'done'
                        : undefined
                  }
                  connector={true}
                  key={'declarantAt'}
                />
                <ChildStatusBlock
                  title={
                    <FormattedMessage
                      defaultMessage="Подготовить счет"
                      id="customsDeliveryOrder.status-label.prepareInvoice"
                      description="Статус заказа"
                    />
                  }
                  subtitle={
                    order.prepareInvoiceAt &&
                    moment(order.prepareInvoiceAt).format('DD.MM.YYYY, HH:mm')
                  }
                  status={
                    order.status === 'prepareInvoice'
                      ? 'active'
                      : order.prepareInvoiceAt
                        ? 'done'
                        : undefined
                  }
                  key={'prepareInvoiceAt'}
                />
              </div>
            }
          />
        ),
      },
      {
        statuses: 'waitingForPayment',
        element: (
          <StatusBlock
            title={
              <FormattedMessage
                defaultMessage="Ожидает оплату"
                id="customsDeliveryOrder.status-label.waitingForPayment"
                description="Статус заказа"
              />
            }
            subtitle={
              order.waitingForPaymentAt &&
              moment(order.waitingForPaymentAt).format('DD.MM.YYYY, HH:mm')
            }
            status={
              order.status === 'waitingForPayment'
                ? 'active'
                : order.waitingForPaymentAt
                  ? 'done'
                  : undefined
            }
            connector={true}
            key={'waitingForPaymentAt'}
          />
        ),
      },
      {
        statuses: 'buyOut',
        element: (
          <StatusBlock
            title={
              <FormattedMessage
                defaultMessage="Выкупить"
                id="customsDeliveryOrder.status-label.buyOut"
                description="Статус заказа"
              />
            }
            subtitle={
              order.buyOutAt &&
              moment(order.buyOutAt).format('DD.MM.YYYY, HH:mm')
            }
            status={
              order.status === 'buyOut'
                ? 'active'
                : order.buyOutAt
                  ? 'done'
                  : undefined
            }
            connector={true}
            key={'buyOutAt'}
          />
        ),
      },
      {
        statuses: 'inWork',
        element: (
          <StatusBlock
            title={
              <FormattedMessage
                defaultMessage="В пути"
                id="customsDeliveryOrder.status-label.inWork"
                description="Статус заказа"
              />
            }
            subtitle={
              order.inWorkAt &&
              moment(order.inWorkAt).format('DD.MM.YYYY, HH:mm')
            }
            status={
              order.status === 'inWork'
                ? 'active'
                : order.inWorkAt
                  ? 'done'
                  : undefined
            }
            connector={true}
            key={'inWorkAt'}
          />
        ),
      },
      {
        statuses: 'inStock',
        element: (
          <StatusBlock
            title={
              <FormattedMessage
                defaultMessage="На складе"
                id="customsDeliveryOrder.status-label.inStock"
                description="Статус заказа"
              />
            }
            subtitle={
              order.inStockAt &&
              moment(order.inStockAt).format('DD.MM.YYYY, HH:mm')
            }
            status={
              order.status === 'inStock'
                ? 'active'
                : order.inStockAt
                  ? 'done'
                  : undefined
            }
            connector={true}
            key={'inStockAt'}
          />
        ),
      },
      {
        statuses: 'sentToRussia',
        element: (
          <StatusBlock
            title={
              <FormattedMessage
                defaultMessage="Отправлен в РФ"
                id="customsDeliveryOrder.status-label.sentToRussia"
                description="Статус заказа"
              />
            }
            subtitle={
              order.sentToRussiaAt &&
              moment(order.sentToRussiaAt).format('DD.MM.YYYY, HH:mm')
            }
            status={
              order.status === 'sentToRussia'
                ? 'active'
                : order.sentToRussiaAt
                  ? 'done'
                  : undefined
            }
            connector={true}
            key={'sentToRussiaAt'}
          />
        ),
      },
      {
        statuses: 'customs',
        element: (
          <StatusBlock
            title={
              <FormattedMessage
                defaultMessage="Таможня"
                id="customsDeliveryOrder.status-label.customs"
                description="Статус заказа"
              />
            }
            subtitle={
              order.customsAt &&
              moment(order.customsAt).format('DD.MM.YYYY, HH:mm')
            }
            status={
              order.status === 'customs'
                ? 'active'
                : order.customsAt
                  ? 'done'
                  : undefined
            }
            connector={true}
            key={'customsAt'}
          />
        ),
      },
      {
        statuses: 'transitWarehouse',
        element: (
          <StatusBlock
            title={
              <FormattedMessage
                defaultMessage="Транзитный склад"
                id="customsDeliveryOrder.status-label.transitWarehouse"
                description="Статус заказа"
              />
            }
            subtitle={
              order.transitWarehouseAt &&
              moment(order.transitWarehouseAt).format('DD.MM.YYYY, HH:mm')
            }
            status={
              order.status === 'transitWarehouse'
                ? 'active'
                : order.transitWarehouseAt
                  ? 'done'
                  : undefined
            }
            connector={true}
            key={'transitWarehouseAt'}
          />
        ),
      },
      {
        statuses: 'transferredToTC',
        element: (
          <StatusBlock
            title={
              <FormattedMessage
                defaultMessage="Передан в ТК"
                id="customsDeliveryOrder.status-label.transferredToTC"
                description="Статус заказа"
              />
            }
            subtitle={
              order.transferredToTCAt &&
              moment(order.transferredToTCAt).format('DD.MM.YYYY, HH:mm')
            }
            status={
              order.status === 'transferredToTC'
                ? 'active'
                : order.transferredToTCAt
                  ? 'done'
                  : undefined
            }
            connector={true}
            key={'transferredToTCAt'}
          />
        ),
      },
      {
        statuses: 'received',
        element: (
          <StatusBlock
            title={
              <FormattedMessage
                defaultMessage="Получен"
                id="customsDeliveryOrder.status-label.received"
                description="Статус заказа"
              />
            }
            subtitle={
              order.receivedAt &&
              moment(order.receivedAt).format('DD.MM.YYYY, HH:mm')
            }
            status={
              order.status === 'received'
                ? 'active'
                : order.receivedAt
                  ? 'done'
                  : undefined
            }
            connector={true}
            key={'receivedAt'}
          />
        ),
      },
      {
        statuses: 'archived',
        element: (
          <StatusBlock
            title={
              <FormattedMessage
                defaultMessage="Архив"
                id="tabs.lable.archive"
                description="Надпись на вкладке/Статус заказа"
              />
            }
            subtitle={
              order.archivedAt &&
              moment(order.archivedAt).format('DD.MM.YYYY, HH:mm')
            }
            status={order.status === 'archived' ? 'active' : undefined}
            key={'archived'}
          />
        ),
      },
    ];

    const currentIndex = statusOrder.findIndex((element) => {
      if (Array.isArray(element.statuses)) {
        return element.statuses.includes(order.status);
      } else {
        return element.statuses === order.status;
      }
    });
    if (currentIndex === -1) {
      return null;
    }

    return {
      current: statusOrder[currentIndex].element,
      previous: statusOrder[currentIndex - 1]?.element ?? null,
      next: statusOrder[currentIndex + 1]?.element ?? null,
    };
  }, [order]);

  return (
    <ul className="d-flex flex-column p-0 mb-0">
      {actualStatuses?.previous}
      {actualStatuses?.current}
      {actualStatuses?.next}
    </ul>
  );
};
